/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { QuestionOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { Page } from '../Page';
import DailyCard from './dailyPlanCard/DailyPlanCard';
import { DailyPlanWrapper, WeekDayWrapper, WeekDay, DailyCardWrapper } from './DailyPlan.style';
import { HeaderActionButtonTwo, FilterSelectInputSmall } from '../Page.styled';
import refreshIcon from '../../assets/images/icons/rotate-right-solid.svg';
import { useAuthStateValue } from '../../context/AuthContext';
import useDailyPlan from './hooks/useDailyPlan';
import confirmModal from '../../core/components/modals/ConfirmModal';

const PAGE_KEY = 'dailyPlan';

const DailyPlan = () => {
  const { loggedUser } = useAuthStateValue();
  const { dataSource, loading, getAllSchedulesInMonth, generateSchedule } = useDailyPlan();

  const { year, month } = useParams();
  const history = useHistory();
  const currentYear = new Date().getFullYear();
  const yearIsValid = parseInt(year, 10) === parseInt(currentYear, 10)
    || parseInt(year, 10) === (parseInt(currentYear, 10) - 1)
    || parseInt(year, 10) === (parseInt(currentYear, 10) + 1);
  const monthIsValid = parseInt(month, 10) > 0 && parseInt(month, 10) < 13;

  const previousMonth = parseInt(month, 10) === 1 ? 12 : parseInt(month, 10) - 1;
  const nextMonth = parseInt(month, 10) === 12 ? 1 : parseInt(month, 10) + 1;
  const currentMonthNumberOfDays = new Date(year, month, 0).getDate();
  const previousMonthNumberOfDays = new Date(month === 1 ? year - 1 : year, month - 1, 0).getDate();
  const firstDayOfCurrentMonthIndex = new Date(year, month - 1, 1).getDay();
  const firstDayOfCurrentMonth = () => {
    if (firstDayOfCurrentMonthIndex === 0) return 7;
    return firstDayOfCurrentMonthIndex;
  };
  const numberOfDaysFromPreviousMonthToShow = firstDayOfCurrentMonth() - 1;
  const firstDayOfPreviousMonthToShow = previousMonthNumberOfDays - numberOfDaysFromPreviousMonthToShow - 1;

  const { t } = useTranslation();

  useEffect(() => {
    if (!year && !month) {
      history.push('/dailyPlan/' + new Date().getFullYear() + '/' + (new Date().getMonth() + 1))
    } else if (year && month && (!monthIsValid || !yearIsValid)) {
      history.push('/404');
    }
  }, []);

  useEffect(() => {
    if (year && month) {
      getAllSchedulesInMonth({ year, month });
    }
  }, [year, month]);

  const refresh = () => {
    if (year && month) {
      getAllSchedulesInMonth({ year, month });
    }
  };

  const yearChangeHandler = (value) => {
    history.push('/dailyPlan/' + value + '/' + month);
  };

  const monthChangeHandler = (value) => {
    history.push('/dailyPlan/' + year + '/' + value);
  }

  const generateDailyScheduleHandler = (year, month, day) => {
    confirmModal({
      centered: true,
      icon: <QuestionOutlined />,
      title: t('dailySchedule.generateDailySchedule'),
      content: t('dailySchedule.generateConfirm'),
      okText: t('buttons.yes'),
      cancelText: t('buttons.no'),
      onOk: async (closeFn) => {
        closeFn();
        await generateSchedule({ year, month, day })
        refresh();
      }
    });
  }

  const renderWeekDaysTitles = () => {
    const titlesArray = [];
    for (let i = 1; i <= 7; i += 1) {
      titlesArray.push(<WeekDay>{t('daysInWeek.' + i)}</WeekDay>)
    }
    return (<>{titlesArray}</>);
  };

  const generateMonthSelectValues = () => {
    const monthsArray = [];
    for (let i = 1; i <= 12; i += 1) {
      monthsArray.push({
        key: i,
        value: i,
        label: t('monthlyPlan.' + i)
      })
    }
    return monthsArray;
  }

  const generateYearSelectValues = () => {
    const currentYear = new Date().getFullYear();
    return [
      {
        key: parseInt(currentYear, 10) - 1,
        value: parseInt(currentYear, 10) - 1,
        label: parseInt(currentYear, 10) - 1
      },
      {
        key: parseInt(currentYear, 10),
        value: parseInt(currentYear, 10),
        label: currentYear
      },
      {
        key: parseInt(currentYear, 10) + 1,
        value: parseInt(currentYear, 10) + 1,
        label: (parseInt(currentYear, 10) + 1).toString()
      }
    ]
  }

  const renderDailyPlanCards = () => {
    const dailyPlanCardsArray = [];
    if (dataSource) {
      for (let i = 1; i <= 42; i += 1) {
        if (numberOfDaysFromPreviousMonthToShow !== 0 && i <= numberOfDaysFromPreviousMonthToShow) {
          dailyPlanCardsArray.push(
            <DailyCard
              key={i}
              day={firstDayOfPreviousMonthToShow + i + 1}
              month={previousMonth}
              currentMonth={parseInt(month, 10)}
              year={parseInt(year, 10)}
            />
          );
        } else if (i <= (currentMonthNumberOfDays + numberOfDaysFromPreviousMonthToShow)) {
          const matchingDataSourceRecord = dataSource?.find((el) => el?.year === parseInt(year, 10) && el?.month === parseInt(month, 10) && el?.day === i - numberOfDaysFromPreviousMonthToShow);
          dailyPlanCardsArray.push(
            <DailyCard
              key={i}
              day={i - numberOfDaysFromPreviousMonthToShow}
              versions={matchingDataSourceRecord?.versions}
              status="test"
              month={parseInt(month, 10)}
              currentMonth={parseInt(month, 10)}
              year={parseInt(year, 10)}
              loading={loading}
              generateDailyScheduleHandler={generateDailyScheduleHandler}
            />
          );
        } else if (i > (currentMonthNumberOfDays + numberOfDaysFromPreviousMonthToShow)) {
          dailyPlanCardsArray.push(
            <DailyCard
              key={i}
              day={i - numberOfDaysFromPreviousMonthToShow - currentMonthNumberOfDays}
              month={nextMonth}
              currentMonth={parseInt(month, 10)}
              year={parseInt(year, 10)}
            />
          );
        }
      }
    }
    return (<>{dailyPlanCardsArray}</>);
  };

  return (
    <Page
      title={t('dailyPlan.title')}
      pageKey={PAGE_KEY}
      headerVisible
      headerActions={(
        <>
          <HeaderActionButtonTwo onClick={refresh} style={{ width: '130px' }}>
            {t('monthlyPlan.refresh')}
            <ReactSVG src={refreshIcon} style={{ marginLeft: '10px', marginTop: '3px', color: '#585858' }} />
          </HeaderActionButtonTwo>
          <FilterSelectInputSmall
            defaultValue={new Date()?.getFullYear()}
            options={generateYearSelectValues()}
            value={generateYearSelectValues()?.find((el) => el?.value === parseInt(year, 10))}
            onChange={yearChangeHandler}
          />
          <FilterSelectInputSmall
            defaultValue={new Date().getMonth() + 1}
            options={generateMonthSelectValues()}
            onChange={monthChangeHandler}
            value={generateMonthSelectValues()?.find((el) => el?.value === parseInt(month, 10))}
          />
        </>
    )}
      content={(
        <DailyPlanWrapper>
          <WeekDayWrapper>
            {renderWeekDaysTitles()}
          </WeekDayWrapper>
          <DailyCardWrapper>
            {renderDailyPlanCards()}
          </DailyCardWrapper>
        </DailyPlanWrapper>
      )}
    />
  );
}

export default DailyPlan;
